import React, {CSSProperties, useEffect, useMemo, useRef} from 'react'
import {
  TwCell,
  TwEmptyContent,
  TwTable,
  TwTableBody,
  TwTableHeader,
  TwTableHeaderCol,
  TwTableRow,
  TwTableScrollContainer
} from '@api/shared/components/table/tw'
import {TableDataType, TableProps, TAccessor, TCellWidth} from './types'

const buildGridWidthForColumn = (width: TCellWidth): string => {
  if (!width || width === 'auto') {
    return 'auto'
  }
  if (typeof width === 'number') {
    return `${width}px`
  } else if (typeof width === 'string') {
    return width
  }
  return '1fr'
}

const accessRow = <T extends TableDataType>(
  row: T,
  index: number,
  accessor: TAccessor<T>
): React.ReactNode => {
  if (typeof accessor === 'function') {
    return accessor(row, index)
  }
  return row[accessor]
}

export const SharedTable = <T extends TableDataType>(props: TableProps<T>) => {
  const headerElRef = useRef<HTMLElement>(null)
  const tableElRef = useRef<HTMLElement>(null)
  const {
    columns,
    rows,
    stickyRows,
    leftPadding,
    rightPadding,
    style,
    emptyContent
  } = props
  const rowStyle = useMemo(() => {
    const gridTemplateColumnsItems = columns.map(column =>
      buildGridWidthForColumn(column.width)
    )
    if (leftPadding) {
      gridTemplateColumnsItems.unshift(buildGridWidthForColumn(leftPadding))
    }
    if (rightPadding) {
      gridTemplateColumnsItems.push(buildGridWidthForColumn(rightPadding))
    }
    return {
      gridTemplateColumns: gridTemplateColumnsItems.join(' ')
    }
  }, [columns, leftPadding, rightPadding])

  const bodyRowStyle = useMemo(() => {
    return {
      gridTemplateColumns: 'var(--table-header-grid-template-columns, auto)'
    }
  }, [])

  useEffect(() => {
    if (headerElRef.current) {
      const headerEl = headerElRef.current
      const applyTemplateColumns = () => {
        const style = getComputedStyle(headerEl)
        /* Set the grid template columns for the body as css variable */
        tableElRef.current?.style.setProperty(
          '--table-header-grid-template-columns',
          style.gridTemplateColumns
        )
      }
      const resizeObserver = new ResizeObserver(applyTemplateColumns)
      resizeObserver.observe(headerEl)
      applyTemplateColumns()

      return () => {
        resizeObserver.disconnect()
      }
    }
    /* We have to return undefined because of eslint rule */
    return undefined
  }, [columns])

  const tableContent = (
    <TwTableScrollContainer style={style}>
      <TwTable ref={tableElRef}>
        <TwTableHeader style={rowStyle} ref={headerElRef}>
          {leftPadding && (
            <TwTableHeaderCol
              key="--empty-space-before--"
              $noBorderSides
              $big
            />
          )}
          {columns.map(column => (
            <TwTableHeaderCol
              key={column.id}
              className={column.col?.className}
              $big
              $sticky={!!column.sticky}
              style={
                typeof column.sticky === 'number'
                  ? ({left: column.sticky} as CSSProperties)
                  : undefined
              }
            >
              {column.title}
            </TwTableHeaderCol>
          ))}
          {rightPadding && (
            <TwTableHeaderCol key="--empty-space-after--" $noBorderSides $big />
          )}
          {rows.length > 0 &&
            stickyRows &&
            stickyRows.map(stickyRow =>
              Array.isArray(stickyRow) ? (
                <>
                  {leftPadding && (
                    <TwTableHeaderCol
                      key="--empty-space-before--"
                      $noBorderSides
                      $borderTop
                    />
                  )}
                  {columns.map((column, index) => (
                    <TwTableHeaderCol
                      key={column.id}
                      className={column.col?.className}
                      $borderTop
                      $sticky={!!column.sticky}
                      style={
                        typeof column.sticky === 'number'
                          ? ({left: column.sticky} as CSSProperties)
                          : undefined
                      }
                    >
                      {stickyRow.length > index && stickyRow[index]}
                    </TwTableHeaderCol>
                  ))}
                  {rightPadding && (
                    <TwTableHeaderCol
                      key="--empty-space-after--"
                      $noBorderSides
                      $borderTop
                    />
                  )}
                </>
              ) : null
            )}
        </TwTableHeader>
        {rows.length > 0 ? (
          <TwTableBody>
            {rows.map(row => (
              <TwTableRow style={bodyRowStyle}>
                {leftPadding && (
                  <TwCell key="--empty-space-before--" $noBorder />
                )}
                {columns.map((column, index) => (
                  <TwCell
                    key={column.id}
                    className={column.cell?.className}
                    $sticky={!!column.sticky}
                    style={
                      typeof column.sticky === 'number'
                        ? ({left: column.sticky} as CSSProperties)
                        : undefined
                    }
                  >
                    {accessRow(row, index, column.accessor)}
                  </TwCell>
                ))}
                {rightPadding && (
                  <TwCell key="--empty-space-after--" $noBorder />
                )}
              </TwTableRow>
            ))}
          </TwTableBody>
        ) : emptyContent ? (
          <TwEmptyContent>{emptyContent}</TwEmptyContent>
        ) : null}
      </TwTable>
    </TwTableScrollContainer>
  )

  return tableContent
}
